import { useCallback, useEffect, useMemo, useState } from "react";
import { Container, useTheme, alpha, Stack, Link, Tooltip } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { GridCellParams, GridRenderCellParams, GridRowClassNameParams, GridRowIdGetter, GridRowModel } from "@mui/x-data-grid-pro";
import { useNavigate, useLocation } from "react-router-dom";
import { DateTime } from "luxon";

import CustomDataGridPro from "components/Common/CustomDataGridPro";
import useOrderActions from "components/Orders/useOrderActions";
import useWindowTitle from "helpers/context/Title/useWindowTitle";
import { useTranslations } from "@fenetech/translations";
import useOrderData from "components/Orders/useOrderData";
import DataGridColumnGenerator from "components/Common/DataGridColumnGenerator";
import useIsMobile from "helpers/hooks/useIsMobile";
import { IProdStatusSummary } from "models/IOrder";
import { ProdStatusTypeEnum } from "helpers/enums";
import useApplicationInfo from "helpers/context/Application/useApplicationInfo";
import useFormatHelper from "helpers/hooks/useFormatHelper";
import Format from "helpers/fv.format";
import ProdStatusUnitHistory from "components/Orders/ProdStatus/ProdStatusUnitHistory";
import ProdStatusUnitRejectHistory from "components/Orders/ProdStatus/ProdStatusUnitRejectHistory";
import useWait from "helpers/context/Page/useWait";
import useActionButtons from "helpers/context/Page/useActionButtons";
import { isNullOrWhiteSpace } from "helpers/objects";

interface IProdStatusRow extends IProdStatusSummary {
    orderItemAndQty: string,
    formattedPart: string,
}

function getStatusType(statusCode: string): ProdStatusTypeEnum {

    switch (statusCode) {
        case "__L__":
        case "__S__":
        case "__TS__":
        case "__D__":
        case "__MD__":
        case "__TR__":
        case "__ML__":
        case "__U__":
        case "__MU__":
        case "__C__":
        case "__ST__":
        case "__MX__":
        case "__UG__":
        case "__MG__":
            return ProdStatusTypeEnum.Complete;
        default:
            return isRejectCode(statusCode) ? ProdStatusTypeEnum.Rejected : ProdStatusTypeEnum.Released;
    }
}

// This logic was pulled from the old usp_biSelectOrderProdStatus (v 10.11)
function isRejectCode(statusCode: string): boolean {
    if (statusCode.length > 0) {
        if (statusCode === "__PB__" || statusCode === "__B__") {
            return true;
        }
        else if (statusCode.length < 4) {
            return true;
        }
        else if (!statusCode.startsWith("__") || !statusCode.endsWith("__")) {
            return true;
        }
    }

    return false;
}

const ProdStatus: React.FC<any> = () => {

    const navigate = useNavigate();
    const query = new URLSearchParams(useLocation().search);
    const oKeyString = query.get("oKey") ?? undefined;

    const [unitHistoryDialogVisible, setUnitHistoryDialogVisible] = useState<boolean>(false);
    const [unitRejectHistoryDialogVisible, setUnitRejectHistoryDialogVisible] = useState<boolean>(false);

    const orderActions = useOrderActions();
    const { order, prodStatus } = useOrderData();
    const theme = useTheme();
    const wait = useWait();
    const isMobile = useIsMobile();
    const tm = useTranslations();
    const appInfo = useApplicationInfo();
    const formatHelper = useFormatHelper();
    const actionButtons = useActionButtons();

    if (!oKeyString) {
        //No okey 
        navigate(-1);
    }

    const oKey = oKeyString ? parseInt(oKeyString) : 0;

    const title = useMemo(() => {
        const title: string[] = [];
        if (order && !isNullOrWhiteSpace(order.orderNumber)) {
            title.push(order.orderNumber);
        }
        title.push(tm.Get("Order Production Status"));
        
        return title;
    }, [tm, order]);

    useWindowTitle(...title);

    useEffect(() => {
        if (oKey) {
            orderActions.LoadOrderAsync(oKey);
            orderActions.LoadProdStatusAsync(oKey);
        }
    }, [oKey, orderActions]);

    useEffect( () => {
        actionButtons.SetBackButton(0, `/orders?oKey=${oKey}`)
    }, [oKey, actionButtons])

    const prodStatusRows = useMemo(() => {
        const details: IProdStatusRow[] = [];

        if (order && prodStatus) {
            prodStatus.forEach((pd) => {
                const props: IProdStatusRow = {
                    ...pd,
                    orderItemAndQty: `${pd.orderNumber} - ${Format.FormatLineItem(pd.lineItem, pd.subLineItem)}/${pd.orderItemXofOrderQty}`,
                    formattedPart: `${Format.FormatPartDescription(pd.partNo, pd.partNoSuffix, pd.description)}`,
                };
                details.push(props);
            });
            
            return details;

        } else {
            return null;
        }

    }, [prodStatus, order]);

    const getStatusText = useCallback((row: IProdStatusRow) => {
        return formatHelper.FormatStatusCode(row.code);
    }, [formatHelper]);

    const getStatusColor = useCallback((row: IProdStatusRow) => {
        const statusType = getStatusType(row.code);
        switch (statusType) {
            case ProdStatusTypeEnum.Complete:
                return theme.palette.success.contrastText;
            case ProdStatusTypeEnum.Rejected:
                return theme.palette.error.contrastText;
            default:
                return undefined;
        }
    }, [theme.palette.success.contrastText, theme.palette.error.contrastText]);


    const onStatusClick = useCallback(async (row: IProdStatusRow) => {
        if (oKey && row.schedID && row.unitID) {
            wait.Show(true);
            await orderActions.LoadProdStatusUnitHistoryAsync(oKey, row);
            setUnitHistoryDialogVisible(true);
            wait.Show(false);
        }
    }, [oKey, wait, orderActions]);

    const getRejectIsVisible = useCallback((row: IProdStatusRow) => {
        const statusType = getStatusType(row.code);
        return statusType === ProdStatusTypeEnum.Rejected && appInfo.parameters.displayProdStatusRejectLink;
    }, [appInfo.parameters.displayProdStatusRejectLink]);

    const onRejectClick = useCallback(async (row: IProdStatusRow) => {
        if (oKey && row.schedID && row.unitID && appInfo.parameters.displayProdStatusRejectLink) {
            wait.Show(true);
            await orderActions.LoadProdStatusUnitRejectHistoryAsync(oKey, row);
            setUnitRejectHistoryDialogVisible(true);
            wait.Show(false);
        }
    }, [oKey, wait, orderActions, appInfo.parameters.displayProdStatusRejectLink]);

    const renderStatusCell = useCallback((params: GridRenderCellParams) => {
        return <>
            <Stack direction="row" gap={1}>
                <Link color={getStatusColor(params.row)} variant="body2" component="button" underline="hover" onClick={() => onStatusClick(params.row)}>
                    {getStatusText(params.row)}
                </Link >
                {getRejectIsVisible(params.row) &&
                    <Tooltip title={tm.Get("Reject Status")}>
                        <Link component="button" underline="none" onClick={() => onRejectClick(params.row)} >
                            <ErrorOutline sx={{ color: theme.palette.error.contrastText }} />
                        </Link>
                    </Tooltip>
                }
            </Stack>
        </>;
    }, [theme.palette.error.contrastText, tm, getRejectIsVisible, getStatusColor, getStatusText, onRejectClick, onStatusClick]);

    const columns = useMemo(() => {
        const generator = new DataGridColumnGenerator(tm, prodStatusRows, theme, isMobile);
        generator.TryAddNumericColumn("schedID", "Schedule", undefined, { flex: 1, hideable: false });
        generator.TryAddNumericColumn("unitID", "Unit", undefined, { flex: 1, hideable: false });
        generator.TryAddNumericColumn("batchID", "Batch", undefined, { flex: 1, hideable: false });
        generator.TryAddNumericColumn("bin", "Bin", undefined, { flex: 1, hideable: false });
        generator.TryAddTextColumn("orderItemAndQty", "Item/Order", { flex: 2 });
        generator.TryAddTextColumn("formattedPart", "Part", { flex: 4 });
        generator.TryAddTextColumn("stationID", "Station", { flex: 2 });
        generator.TryAddDateTimeColumn("dateTime", "Date", DateTime.DATETIME_SHORT_WITH_SECONDS, { flex: 3 });
        generator.TryAddRenderCellColumn("code", "Status", renderStatusCell, { flex: 2 });
        generator.TryAddTextColumn("containerID", "Container", { flex: 1 });
        return generator.GetColumns();
    }, [tm, prodStatusRows, theme, isMobile, renderStatusCell]);

    const getRowId: GridRowIdGetter = (row: GridRowModel) => {
        const prodStatus = row as IProdStatusSummary;
        return `${prodStatus.schedID}'${prodStatus.unitID}'${prodStatus.masterKey}'${prodStatus.parentKey}`;
    };

    const getRowClassName = (params: GridRowClassNameParams<any>): string => {
        const prodStatus = params.row as IProdStatusRow;
        return prodStatus.shipsSeparately ? "shipSeparate" : "";
    };

    const getCellClassName = (params: GridCellParams<any>) => {
        if (params.field === "code") {
            const prodStatus = params.row as IProdStatusRow;
            const statusType = getStatusType(prodStatus.code);
            if (statusType === ProdStatusTypeEnum.Complete) {
                return "completed";
            } else if (statusType === ProdStatusTypeEnum.Rejected) {
                return "rejected"
            }
            return "";
        }
        return "";
    };

    return <>
        <Container maxWidth="xl" sx={{ marginTop: 1 }}>
            <CustomDataGridPro
                getRowId={getRowId}
                columns={columns}
                rows={prodStatusRows}
                getRowClassName={getRowClassName}
                getCellClassName={getCellClassName}
                sx={(theme) => {
                    return {
                        "& .shipSeparate": {
                            backgroundColor: alpha(theme.palette.primary.light, 0.2),
                        },
                        "& .completed": {
                            backgroundColor: theme.palette.success.main,
                        },
                        "& .rejected": {
                            backgroundColor: theme.palette.error.main,
                        },
                        "& .MuiDataGrid-iconButtonContainer, .MuiDataGrid-menuIcon": {
                            "& svg": {
                                fill: theme.palette.secondary.contrastText,
                            }
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            background: theme.palette.secondary.main,
                            color: theme.palette.secondary.contrastText
                        },

                    }
                }}
                tableName="ProdStatus"
                tableSubName={isMobile ? "Mobile" : ""}
                hideInMobileColumns={[
                    "batchID",
                    "bin",
                    "orderItemAndQty",
                    "stationID",
                    "containerID"
                ]}
            />

        </Container>

        <ProdStatusUnitHistory
            dialogVisible={unitHistoryDialogVisible}
            onClose={() => setUnitHistoryDialogVisible(false)}
        />

        <ProdStatusUnitRejectHistory
            dialogVisible={unitRejectHistoryDialogVisible}
            onClose={() => setUnitRejectHistoryDialogVisible(false)}
        />
    </>;

};

export default ProdStatus;